import { Track } from "@/src/lib/tracking";
import { ACCOUNT_READ_SCHEMA_VERSION } from "./accountReadTypes";
import type {
  AccountReadBlock,
  AccountReadDimension,
  AccountReadDimensionKey,
  AccountReadGeneralBlockKey,
  AccountReadReport,
  AccountReadSide,
  AccountReadUnreadSummary,
  AccountReadWatchOption,
  AccountReadMetrics,
  AccountReadMetricValue,
} from "./accountReadTypes";

const KNOWN_GENERAL_BLOCK_KEYS: AccountReadGeneralBlockKey[] = [
  "coverage",
  "net_worth",
  "account_balances",
  "monthly_cashflow",
  "spending_categories",
  "portfolio_value",
  "trading_cost",
];

const ALLOWED_BANKING_DIMENSIONS = new Set<string>([
  "subscriptions",
  "idle_cash",
  "runway",
  "annual_bills",
  /** legacy naming from pre-v2 fixtures */
  "big_bills",
]);

const ALLOWED_INVEST_DIMENSIONS = new Set<string>([
  "portfolio_mix",
  "sell_rule",
  "tax_dates",
]);

const EXCLUDED_DIMENSIONS = new Set<string>(["look_through", "fund_fees"]);

function normalizePublishedAt(raw: unknown): string {
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
    return new Date(raw).toISOString();
  }
  if (typeof raw === "string") {
    const value = raw.trim();
    if (/^\d+$/.test(value)) {
      const epochMs = Number(value);
      if (Number.isFinite(epochMs) && epochMs > 0) {
        return new Date(epochMs).toISOString();
      }
    }
    return value;
  }
  return "";
}

function normalizeMetricValue(
  raw: unknown,
): AccountReadMetricValue | undefined {
  if (raw === null || typeof raw === "string" || typeof raw === "boolean") {
    return raw;
  }
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : undefined;
  }
  if (Array.isArray(raw)) {
    const values: AccountReadMetricValue[] = [];
    for (const item of raw) {
      const value = normalizeMetricValue(item);
      if (value === undefined) {
        return undefined;
      }
      values.push(value);
    }
    return values;
  }
  if (raw && typeof raw === "object") {
    const value: Record<string, AccountReadMetricValue> = {};
    for (const [key, item] of Object.entries(raw)) {
      const normalized = normalizeMetricValue(item);
      if (normalized === undefined) {
        return undefined;
      }
      value[key] = normalized;
    }
    return value;
  }
  return undefined;
}

function normalizeMetrics(
  raw: Record<string, unknown>,
  excludedKeys: ReadonlySet<string>,
): AccountReadMetrics {
  const metrics: AccountReadMetrics = {};
  for (const [key, rawValue] of Object.entries(raw)) {
    if (excludedKeys.has(key)) {
      continue;
    }
    const value = normalizeMetricValue(rawValue);
    if (value !== undefined) {
      metrics[key] = value;
    }
  }
  return metrics;
}

function normalizeWatchOption(
  raw: unknown,
): AccountReadWatchOption | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const obj = raw as Record<string, unknown>;
  const key = String(obj.key ?? "");
  if (!key) {
    return undefined;
  }

  const watchKind =
    obj.watch_kind === "needs_chat" || obj.watchKind === "needs_chat"
      ? "needs_chat"
      : "self_running";

  return {
    key,
    label: String(obj.label ?? ""),
    sub: String(obj.sub ?? ""),
    watchKind,
    active: Boolean(obj.active),
    trackerId: obj.tracker_id
      ? String(obj.tracker_id)
      : obj.trackerId
        ? String(obj.trackerId)
        : undefined,
  };
}

export function adaptAccountReadDetail(raw: unknown): AccountReadReport | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const dto = raw as Record<string, unknown>;

  const rawReportId = dto.report_id ?? dto.reportId ?? dto.read_id;
  const reportId =
    typeof rawReportId === "string" ? rawReportId : String(rawReportId || "");
  const side = dto.side as AccountReadSide;

  const rawSchemaVersion = dto.schema_version ?? dto.schemaVersion;
  // Existing persisted v2 reports remain readable through the legacy branch.
  const schemaVersion =
    rawSchemaVersion === undefined || rawSchemaVersion === null
      ? undefined
      : String(rawSchemaVersion);
  if (schemaVersion && schemaVersion !== ACCOUNT_READ_SCHEMA_VERSION) {
    Track.error(new Error(`acct_read_unknown_schema: ${schemaVersion}`), {
      report_id: reportId,
      schema_version: schemaVersion,
    });
    return null;
  }
  const rawSourceDataVersion = dto.source_data_version ?? dto.sourceDataVersion;
  const sourceDataVersion =
    typeof rawSourceDataVersion === "string" ? rawSourceDataVersion : "";

  if (side !== "BANKING" && side !== "INVEST") {
    Track.error(new Error(`acct_read_invalid_side: ${String(dto.side)}`), {
      report_id: reportId,
      side: String(dto.side),
    });
    return null;
  }

  const headline = String(dto.headline ?? "");
  const summaryText = Array.isArray(dto.summary_text)
    ? dto.summary_text.map(String)
    : Array.isArray(dto.summaryText)
      ? dto.summaryText.map(String)
      : [];

  const rawPublishedAt = dto.published_at ?? dto.publishedAt;
  const publishedAt = normalizePublishedAt(rawPublishedAt);

  const content = (
    dto.content && typeof dto.content === "object" ? dto.content : {}
  ) as Record<string, unknown>;

  const blocks: AccountReadBlock[] = [];
  const generalRead = (
    content.general_read && typeof content.general_read === "object"
      ? content.general_read
      : {}
  ) as Record<string, unknown>;

  for (const blockKey of KNOWN_GENERAL_BLOCK_KEYS) {
    const blockVal = generalRead[blockKey];
    if (blockVal && typeof blockVal === "object" && "summary" in blockVal) {
      const block = blockVal as Record<string, unknown>;
      const summary = String(block.summary ?? "");
      if (summary) {
        blocks.push({
          key: blockKey,
          summary,
          metrics: normalizeMetrics(block, new Set(["summary"])),
        });
      }
    }
  }

  const dimensions: AccountReadDimension[] = [];
  const allowedDims =
    side === "BANKING" ? ALLOWED_BANKING_DIMENSIONS : ALLOWED_INVEST_DIMENSIONS;

  const globalWatchOptions: AccountReadWatchOption[] = [];
  const rawWatchOptions = Array.isArray(dto.watch_options)
    ? dto.watch_options
    : Array.isArray(content.watch_options)
      ? content.watch_options
      : [];
  for (const item of rawWatchOptions) {
    const opt = normalizeWatchOption(item);
    if (opt) {
      globalWatchOptions.push(opt);
    }
  }

  for (const [rawKey, rawDimVal] of Object.entries(content)) {
    if (
      rawKey === "general_read" ||
      rawKey === "watch_options" ||
      rawKey === "active_watches"
    ) {
      continue;
    }

    if (EXCLUDED_DIMENSIONS.has(rawKey)) {
      Track.error(new Error(`acct_read_excluded_dimension: ${rawKey}`), {
        report_id: reportId,
        side,
        dim_keys: rawKey,
      });
      continue;
    }

    const dimKey: string = rawKey === "annual_bills" ? "big_bills" : rawKey;
    if (!allowedDims.has(dimKey)) {
      Track.error(new Error(`acct_read_unknown_dimension: ${rawKey}`), {
        report_id: reportId,
        side,
        dim_keys: rawKey,
      });
      continue;
    }

    if (!rawDimVal || typeof rawDimVal !== "object") {
      continue;
    }

    const dimObj = rawDimVal as Record<string, unknown>;
    const narrative = String(dimObj.summary ?? "");

    const metrics = normalizeMetrics(
      dimObj,
      new Set(["summary", "watch_option", "watchOption"]),
    );

    let watchOption =
      normalizeWatchOption(dimObj.watch_option) ??
      normalizeWatchOption(dimObj.watchOption);
    if (!watchOption && globalWatchOptions.length > 0) {
      watchOption = globalWatchOptions.find(
        (o) => o.key === `watch_${dimKey}` || o.key.includes(dimKey),
      );
    }

    dimensions.push({
      key: dimKey as AccountReadDimensionKey,
      narrative,
      metrics,
      watchOption,
    });
  }

  const activeWatches: AccountReadWatchOption[] = [];
  const rawActiveWatches = Array.isArray(dto.active_watches)
    ? dto.active_watches
    : Array.isArray(content.active_watches)
      ? content.active_watches
      : [];
  for (const item of rawActiveWatches) {
    const opt = normalizeWatchOption(item);
    if (opt) {
      activeWatches.push(opt);
    }
  }

  return {
    reportId,
    schemaVersion,
    sourceDataVersion,
    side,
    headline,
    summaryText,
    publishedAt,
    blocks,
    dimensions,
    activeWatches,
  };
}

export function adaptAccountReadUnreadList(
  dtos: unknown,
): AccountReadUnreadSummary[] {
  if (!Array.isArray(dtos)) {
    return [];
  }

  const bySide = new Map<AccountReadSide, AccountReadUnreadSummary>();

  for (const raw of dtos) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const item = raw as Record<string, unknown>;
    const side = item.side as AccountReadSide;
    if (side !== "BANKING" && side !== "INVEST") {
      continue;
    }

    const rawReportId = item.report_id ?? item.reportId ?? item.read_id;
    const reportId =
      typeof rawReportId === "string" ? rawReportId : String(rawReportId || "");
    if (!reportId) {
      continue;
    }

    const headline = String(item.headline ?? "");
    const rawPublishedAt = item.published_at ?? item.publishedAt;
    const publishedAt = normalizePublishedAt(rawPublishedAt);

    const rawSummary = item.summary ?? item.summary_text;
    const summary: AccountReadUnreadSummary = {
      reportId,
      side,
      headline,
      summary: typeof rawSummary === "string" ? rawSummary : undefined,
      publishedAt,
    };

    const existing = bySide.get(side);
    if (!existing) {
      bySide.set(side, summary);
    } else {
      const existingTime = new Date(existing.publishedAt).getTime();
      const currentTime = new Date(summary.publishedAt).getTime();
      if (Number.isNaN(existingTime) || currentTime > existingTime) {
        bySide.set(side, summary);
      }
    }
  }

  return Array.from(bySide.values()).sort((a, b) => {
    const timeA = new Date(a.publishedAt).getTime();
    const timeB = new Date(b.publishedAt).getTime();
    if (Number.isNaN(timeA) || Number.isNaN(timeB)) {
      return 0;
    }
    return timeA - timeB;
  });
}
